import * as signalR from '@microsoft/signalr';
import Config from '@/lib/Config';
import UserSessionState from '@/lib/SessionState';

export interface PlanAlteredPayload {
    locationId: string;
    changedAt: string;
}

export interface ToDoActionsAlteredPayload {
    mineAreaId: string;
    departmentId: string | null;
}

export interface LocationPriorityAlteredPayload {
    locationId: string;
    plannedLocationId: string;
    priority: number;
}

export interface LocationHazardsAlteredPayload {
    locationId: string;
}

export interface LocationsAddedToPlanPayload {
    planId: string;
    locationIds: string[];
}

export interface LocationsRemovedFromPlanPayload {
    planId: string;
    locationIds: string[];
}

export interface EquipmentHazardsAlteredPayload {
    equipmentId: string;
}

export interface EquipmentAvailabilityAlteredPayload {
    equipmentId: string | null;
    affectedFrom: string;
    affectedTo: string;
}

export interface LocationAvailabilityAlteredPayload {
    locationId: string;
}

export interface TaskEquipmentAssignedPayload {
    locationIds: string[];
}

/**
 * Maps each hub notification method name to the payload shape the server sends for it. Add an entry
 * here when introducing a new notification type so callers get compile-time method-name and payload
 * checking for free.
 */
export interface HubNotificationPayloadMap {
    PlanAltered: PlanAlteredPayload;
    ToDoActionsAltered: ToDoActionsAlteredPayload;
    LocationPriorityAltered: LocationPriorityAlteredPayload;
    LocationHazardsAltered: LocationHazardsAlteredPayload;
    LocationsAddedToPlan: LocationsAddedToPlanPayload;
    LocationsRemovedFromPlan: LocationsRemovedFromPlanPayload;
    LocationAvailabilityAltered: LocationAvailabilityAlteredPayload;
    EquipmentHazardsAltered: EquipmentHazardsAlteredPayload;
    EquipmentAvailabilityAltered: EquipmentAvailabilityAlteredPayload;
    TaskEquipmentAssigned: TaskEquipmentAssignedPayload;
}

/** The set of notification method names callers are allowed to subscribe to. */
export type HubNotificationMethod = keyof HubNotificationPayloadMap;

export type HubNotificationHandler<T> = (payload: T) => void;

/**
 * A subscription to a single notification type on the shared hub connection. Disposing it removes
 * the handler and releases this consumer's hold on the shared connection.
 */
export interface HubSubscription {
    dispose(): Promise<void>;
}

const SIGNALR_CONNECTION_ID_HEADER = 'x-signalr-connection-id';

const sessionState = new UserSessionState();

const baseUrlFromConfig = /\/$/.test(Config.ApiBasePath ?? '') ? Config.ApiBasePath : (Config.ApiBasePath ?? '') + '/';

/**
 * A single shared SignalR connection that any component or store can subscribe to for specific
 * notification types. The underlying connection is reference-counted so it is established once and
 * torn down only when the last consumer releases it.
 */
class HubNotificationsService {
    private _connection: signalR.HubConnection | null = null;
    private _startPromise: Promise<void> | null = null;
    private _refCount = 0;

    private buildConnection(): signalR.HubConnection {
        return new signalR.HubConnectionBuilder()
            .withUrl(`${baseUrlFromConfig}hubs/plan-notifications`, {
                accessTokenFactory: () => sessionState.idToken ?? '',
                withCredentials: false,
            })
            .withAutomaticReconnect()
            .build();
    }

    public async start(): Promise<void> {
        this._refCount++;

        // A connection is already established or being established: wait for it
        // instead of returning before the socket is actually up.
        if (this._startPromise) {
            await this._startPromise;
            return;
        }

        this._connection = this.buildConnection();
        this._startPromise = this._connection.start();

        try {
            await this._startPromise;
        } catch (e) {
            this._connection = null;
            this._startPromise = null;
            this._refCount = Math.max(0, this._refCount - 1);
            throw e;
        }
    }

    public async stop(): Promise<void> {
        this._refCount = Math.max(0, this._refCount - 1);

        // Other consumers are still using the shared connection (e.g. a new
        // board mounted during a shift-navigation swap before this one was
        // destroyed). Keep the connection alive.
        if (this._refCount > 0) return;

        if (!this._connection) return;

        const connection = this._connection;
        this._connection = null;
        this._startPromise = null;

        await connection.stop();
    }

    /**
     * Registers a handler for a specific notification type on the shared connection. The connection
     * must already be started (see {@link start} or {@link subscribe}).
     */
    public on<M extends HubNotificationMethod>(
        methodName: M,
        handler: HubNotificationHandler<HubNotificationPayloadMap[M]>,
    ): void {
        if (!this._connection) return;
        this._connection.on(methodName, handler as (...args: any[]) => void);
    }

    /** Removes a previously registered handler for a specific notification type. */
    public off<M extends HubNotificationMethod>(
        methodName: M,
        handler: HubNotificationHandler<HubNotificationPayloadMap[M]>,
    ): void {
        if (!this._connection) return;
        this._connection.off(methodName, handler as (...args: any[]) => void);
    }

    /**
     * Convenience helper that starts (or joins) the shared connection and registers a handler for a
     * specific notification type, returning a subscription whose {@link HubSubscription.dispose}
     * removes the handler and releases the connection.
     */
    public async subscribe<M extends HubNotificationMethod>(
        methodName: M,
        handler: HubNotificationHandler<HubNotificationPayloadMap[M]>,
    ): Promise<HubSubscription> {
        await this.start();
        this.on(methodName, handler);

        let disposed = false;
        return {
            dispose: async () => {
                if (disposed) return;
                disposed = true;
                this.off(methodName, handler);
                await this.stop();
            },
        };
    }

    public get connectionId(): string | null {
        return this._connection?.connectionId ?? null;
    }

    public get headerName(): string {
        return SIGNALR_CONNECTION_ID_HEADER;
    }
}

const HubNotifications = new HubNotificationsService();
export default HubNotifications;
